import { useState } from 'react'
import {
  CloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  RadioGroup,
  Checkbox,
  Badge,
  Separator,
  Dialog,
  type RadioGroupValueChangeDetails,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiArrowRight } from 'react-icons/fi'
import { useMergeBusinesses, useMergeWorksites } from '../../hooks/useBusiness'
import type { BusinessSummary, WorksiteSummary } from '../../types/business'
import { ResponsiveModal } from '../shared'

interface MergeBusinessModalProps {
  isOpen: boolean
  onClose: () => void
  items: BusinessSummary[]
}

export function MergeBusinessModal({ isOpen, onClose, items }: MergeBusinessModalProps) {
  const mergeMutation = useMergeBusinesses()
  const [canonicalId, setCanonicalId] = useState(items[0]?.id ?? '')
  const [selectedDupes, setSelectedDupes] = useState<Set<string>>(
    () => new Set(items.slice(1).map(i => i.id))
  )

  const toggleDupe = (id: string) => {
    setSelectedDupes(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleMerge = async () => {
    const dupes = Array.from(selectedDupes).filter(id => id !== canonicalId)
    if (dupes.length === 0) {
      toaster.create({ title: 'Select at least one duplicate to merge', type: 'warning', duration: 3000 })
      return
    }

    try {
      const result = await mergeMutation.mutateAsync({ canonicalId, duplicateIds: dupes })
      toaster.create({
        title: 'Merge complete',
        description: `${result.duplicatesRemoved} duplicate(s) merged, ${result.worksitesMoved} worksite(s) moved`,
        type: 'success',
        duration: 5000,
      })
      onClose()
    } catch {
      toaster.create({ title: 'Merge failed', type: 'error', duration: 3000 })
    }
  }

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} size="lg">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>Merge Businesses</Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Body>
            <VStack gap={4} align="stretch">
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  Select the canonical business (the one to keep):
                </Text>
                <RadioGroup.Root size="sm" value={canonicalId} onValueChange={(details: RadioGroupValueChangeDetails) => setCanonicalId(details.value ?? '')}>
                  <VStack align="stretch" gap={1}>
                    {items.map(item => (
                      <HStack
                        key={item.id}
                        p={2}
                        bg={item.id === canonicalId ? 'accent.soft' : 'gray.50'}
                        borderRadius="md"
                        borderWidth={item.id === canonicalId ? '2px' : '1px'}
                        borderColor={item.id === canonicalId ? 'accent' : 'gray.200'}
                      >
                        <RadioGroup.Item value={item.id}><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /></RadioGroup.Item>
                        <Box flex={1}>
                          <Text fontSize="sm" fontWeight="medium">{item.label}</Text>
                          <HStack gap={2}>
                            {item.type && <Badge colorPalette="purple" fontSize="2xs">{item.type}</Badge>}
                            <Text fontSize="xs" color="gray.500">
                              {item.worksiteCount} location{item.worksiteCount !== 1 ? 's' : ''}
                            </Text>
                          </HStack>
                        </Box>
                        {item.id === canonicalId && (
                          <Badge bg="info.subtle" color="info.fg" fontSize="2xs">Keep</Badge>
                        )}
                      </HStack>
                    ))}
                  </VStack>
                </RadioGroup.Root>
              </Box>

              <Separator />

              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  Select duplicates to merge into the canonical:
                </Text>
                <VStack align="stretch" gap={1}>
                  {items.filter(i => i.id !== canonicalId).map(item => (
                    <HStack key={item.id} p={2} bg="orange.50" borderRadius="md">
                      <Checkbox.Root
                        size="sm"
                        onCheckedChange={() => toggleDupe(item.id)}
                        checked={selectedDupes.has(item.id)}><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control></Checkbox.Root>
                      <Text fontSize="sm">{item.label}</Text>
                      <FiArrowRight size={12} color="gray" />
                      <Text fontSize="xs" color="gray.500" fontStyle="italic">
                        {items.find(i => i.id === canonicalId)?.label}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>

              <Box bg="gray.50" p={3} borderRadius="md">
                <Text fontSize="xs" color="gray.600">
                  Merging will move all worksites and union affiliations from the selected duplicates
                  into the canonical business, then deactivate the duplicates.
                </Text>
              </Box>
            </VStack>
          </Dialog.Body>
          <Dialog.Footer>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button
              colorPalette="orange"
              onClick={handleMerge}
              loading={mergeMutation.isPending}
              disabled={selectedDupes.size === 0}
            >
              Merge {selectedDupes.size} Duplicate{selectedDupes.size !== 1 ? 's' : ''}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  );
}

interface MergeWorksiteModalProps {
  isOpen: boolean
  onClose: () => void
  items: WorksiteSummary[]
}

export function MergeWorksiteModal({ isOpen, onClose, items }: MergeWorksiteModalProps) {
  const mergeMutation = useMergeWorksites()
  const [canonicalId, setCanonicalId] = useState(items[0]?.id ?? '')
  const [selectedDupes, setSelectedDupes] = useState<Set<string>>(
    () => new Set(items.slice(1).map(i => i.id))
  )

  const toggleDupe = (id: string) => {
    setSelectedDupes(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleMerge = async () => {
    const dupes = Array.from(selectedDupes).filter(id => id !== canonicalId)
    if (dupes.length === 0) {
      toaster.create({ title: 'Select at least one duplicate to merge', type: 'warning', duration: 3000 })
      return
    }

    try {
      const result = await mergeMutation.mutateAsync({ canonicalId, duplicateIds: dupes })
      toaster.create({
        title: 'Merge complete',
        description: `${result.duplicatesRemoved} duplicate(s) merged, ${result.employmentsMoved} employment(s) moved`,
        type: 'success',
        duration: 5000,
      })
      onClose()
    } catch {
      toaster.create({ title: 'Merge failed', type: 'error', duration: 3000 })
    }
  }

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} size="lg">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>Merge Worksites</Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Body>
            <VStack gap={4} align="stretch">
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  Select the canonical worksite (the one to keep):
                </Text>
                <RadioGroup.Root size="sm" value={canonicalId} onValueChange={(details: RadioGroupValueChangeDetails) => setCanonicalId(details.value ?? '')}>
                  <VStack align="stretch" gap={1}>
                    {items.map(item => (
                      <HStack
                        key={item.id}
                        p={2}
                        bg={item.id === canonicalId ? 'accent.soft' : 'gray.50'}
                        borderRadius="md"
                        borderWidth={item.id === canonicalId ? '2px' : '1px'}
                        borderColor={item.id === canonicalId ? 'accent' : 'gray.200'}
                      >
                        <RadioGroup.Item value={item.id}><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /></RadioGroup.Item>
                        <Box flex={1}>
                          <Text fontSize="sm" fontWeight="medium">{item.label}</Text>
                          <HStack gap={2}>
                            {item.type && <Badge colorPalette="purple" fontSize="2xs">{item.type}</Badge>}
                            {item.city && <Text fontSize="xs" color="gray.500">{item.city}{item.state ? `, ${item.state}` : ''}</Text>}
                            <Text fontSize="xs" color="gray.500">
                              {item.employeeCount} employee{item.employeeCount !== 1 ? 's' : ''}
                            </Text>
                          </HStack>
                        </Box>
                        {item.id === canonicalId && (
                          <Badge bg="info.subtle" color="info.fg" fontSize="2xs">Keep</Badge>
                        )}
                      </HStack>
                    ))}
                  </VStack>
                </RadioGroup.Root>
              </Box>

              <Separator />

              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  Select duplicates to merge into the canonical:
                </Text>
                <VStack align="stretch" gap={1}>
                  {items.filter(i => i.id !== canonicalId).map(item => (
                    <HStack key={item.id} p={2} bg="orange.50" borderRadius="md">
                      <Checkbox.Root
                        size="sm"
                        onCheckedChange={() => toggleDupe(item.id)}
                        checked={selectedDupes.has(item.id)}><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control></Checkbox.Root>
                      <Text fontSize="sm">{item.label}</Text>
                      <FiArrowRight size={12} color="gray" />
                      <Text fontSize="xs" color="gray.500" fontStyle="italic">
                        {items.find(i => i.id === canonicalId)?.label}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>

              <Box bg="gray.50" p={3} borderRadius="md">
                <Text fontSize="xs" color="gray.600">
                  Merging will move all employees and union affiliations from the selected duplicates
                  into the canonical worksite, then deactivate the duplicates.
                </Text>
              </Box>
            </VStack>
          </Dialog.Body>
          <Dialog.Footer>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button
              colorPalette="orange"
              onClick={handleMerge}
              loading={mergeMutation.isPending}
              disabled={selectedDupes.size === 0}
            >
              Merge {selectedDupes.size} Duplicate{selectedDupes.size !== 1 ? 's' : ''}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  );
}
